import * as v from 'valibot'

//
// SSE events types
//

export type AnyEventVariant = typeof AnyEventVariant[keyof typeof AnyEventVariant] // prettier-ignore
export const AnyEventVariant = {
  system: 'system',
  data: 'data',
} as const

export type SystemEventType = typeof SystemEventType[keyof typeof SystemEventType] // prettier-ignore
export const SystemEventType = {
  close: 'CLOSE',
  connected: 'CONNECTED',
  ping: 'PING',
  reconnect: 'RECONNECT',
} as const

export type DataEventType = typeof DataEventType[keyof typeof DataEventType] // prettier-ignore
export const DataEventType = {
  channelPurchase: 'CHANNEL_PURCHASE_EVENT',
  channelSetPurchase: 'CHANNEL_SET_PURCHASE_EVENT',
  episodePurchase: 'EPISODE_PURCHASE_EVENT',
  liveEventPurchase: 'LIVE_EVENT_PURCHASE_EVENT',
  partnerProductPurchase: 'PARTNER_PRODUCT_PURCHASE_EVENT',
  seasonPurchase: 'SEASON_PURCHASE_EVENT',
  subscriptionPurchase: 'SUBSCRIPTION_PURCHASE_EVENT',
  tvShowPurchase: 'TV_SHOW_PURCHASE_EVENT',
  tvShowSetPurchase: 'TV_SHOW_SET_PURCHASE_EVENT',
  vodPurchase: 'VOD_PURCHASE_EVENT',
  vodSetPurchase: 'VOD_SET_PURCHASE_EVENT',
} as const

//
// `system` control events from backend
//

/**
 * SSE system => `CLOSE` event
 */
export type SystemEventClose = v.InferOutput<typeof SystemEventClose> // prettier-ignore
export const SystemEventClose = v.object({
  type: v.literal(SystemEventType.close),
})

/**
 * SSE system => `CONNECTED` event
 */
export type SystemEventConnected = v.InferOutput<typeof SystemEventConnected> // prettier-ignore
export const SystemEventConnected = v.object({
  type: v.literal(SystemEventType.connected),
})

/**
 * SSE system => `PING` event
 */
export type SystemEventPing = v.InferOutput<typeof SystemEventPing> // prettier-ignore
export const SystemEventPing = v.object({
  type: v.literal(SystemEventType.ping),
})

/**
 * SSE system => `RECONNECT` event
 */
export type SystemEventReconnect = v.InferOutput<typeof SystemEventReconnect> // prettier-ignore
export const SystemEventReconnect = v.object({
  type: v.literal(SystemEventType.reconnect),
  retry: v.optional(v.number()), // reconnection timeout in milliseconds
})

/**
 * SSE general system event
 */
export type SystemEvent = v.InferOutput<typeof SystemEvent> // prettier-ignore
export const SystemEvent = v.object({
  id: v.optional(v.string()),
  event: v.literal(AnyEventVariant.system),
  data: v.variant('type', [
    SystemEventClose,
    SystemEventConnected,
    SystemEventPing,
    SystemEventReconnect,
  ]),
})

//
// `data` logic events
//

/**
 * SSE data => `CHANNEL_PURCHASE_EVENT` event
 */
export type DataEventChannelPurchase = v.InferOutput<typeof DataEventChannelPurchase> // prettier-ignore
export const DataEventChannelPurchase = v.object({
  type: v.literal(DataEventType.channelPurchase),
  itemId: v.number(),
})

/**
 * SSE data => `CHANNEL_SET_PURCHASE_EVENT` event
 */
export type DataEventChannelSetPurchase = v.InferOutput<typeof DataEventChannelSetPurchase> // prettier-ignore
export const DataEventChannelSetPurchase = v.object({
  type: v.literal(DataEventType.channelSetPurchase),
  itemId: v.number(),
})

/**
 * SSE data => `EPISODE_PURCHASE_EVENT` event
 */
export type DataEventEpisodePurchase = v.InferOutput<typeof DataEventEpisodePurchase> // prettier-ignore
export const DataEventEpisodePurchase = v.object({
  type: v.literal(DataEventType.episodePurchase),
  itemId: v.number(),
  metadata: v.object({
    tvShowId: v.number(),
    seasonId: v.optional(v.number()),
  }),
})

/**
 * SSE data => `LIVE_EVENT_PURCHASE_EVENT` event
 */
export type DataEventLiveEventPurchase = v.InferOutput<typeof DataEventLiveEventPurchase> // prettier-ignore
export const DataEventLiveEventPurchase = v.object({
  type: v.literal(DataEventType.liveEventPurchase),
  itemId: v.number(),
})

/**
 * SSE data => `PARTNER_PRODUCT_PURCHASE_EVENT` event
 */
export type DataEventPartnerProductPurchase = v.InferOutput<typeof DataEventPartnerProductPurchase> // prettier-ignore
export const DataEventPartnerProductPurchase = v.object({
  type: v.literal(DataEventType.partnerProductPurchase),
  itemId: v.number(),
})

/**
 * SSE data => `SEASON_PURCHASE_EVENT` event
 */
export type DataEventSeasonPurchase = v.InferOutput<typeof DataEventSeasonPurchase> // prettier-ignore
export const DataEventSeasonPurchase = v.object({
  type: v.literal(DataEventType.seasonPurchase),
  itemId: v.number(),
  metadata: v.object({
    tvShowId: v.number(),
  }),
})

/**
 * SSE data => `SUBSCRIPTION_PURCHASE_EVENT` event
 */
export type DataEventSubscriptionPurchase = v.InferOutput<typeof DataEventSubscriptionPurchase> // prettier-ignore
export const DataEventSubscriptionPurchase = v.object({
  type: v.literal(DataEventType.subscriptionPurchase),
  itemId: v.number(),
})

/**
 * SSE data => `TV_SHOW_PURCHASE_EVENT` event
 */
export type DataEventTvShowPurchase = v.InferOutput<typeof DataEventTvShowPurchase> // prettier-ignore
export const DataEventTvShowPurchase = v.object({
  type: v.literal(DataEventType.tvShowPurchase),
  itemId: v.number(),
})

/**
 * SSE data => `TV_SHOW_SET_PURCHASE_EVENT` event
 */
export type DataEventTvShowSetPurchase = v.InferOutput<typeof DataEventTvShowSetPurchase> // prettier-ignore
export const DataEventTvShowSetPurchase = v.object({
  type: v.literal(DataEventType.tvShowSetPurchase),
  itemId: v.number(),
})

/**
 * SSE data => `VOD_PURCHASE_EVENT` event
 */
export type DataEventVodPurchase = v.InferOutput<typeof DataEventVodPurchase> // prettier-ignore
export const DataEventVodPurchase = v.object({
  type: v.literal(DataEventType.vodPurchase),
  itemId: v.number(),
})

/**
 * SSE data => `VOD_SET_PURCHASE_EVENT` event
 */
export type DataEventVodSetPurchase = v.InferOutput<typeof DataEventVodSetPurchase> // prettier-ignore
export const DataEventVodSetPurchase = v.object({
  type: v.literal(DataEventType.vodSetPurchase),
  itemId: v.number(),
})

/**
 * SSE general data event
 */
export type DataEvent = v.InferOutput<typeof DataEvent> // prettier-ignore
export const DataEvent = v.object({
  id: v.optional(v.string()),
  event: v.literal(AnyEventVariant.data),
  data: v.variant('type', [
    DataEventChannelPurchase,
    DataEventChannelSetPurchase,
    DataEventEpisodePurchase,
    DataEventLiveEventPurchase,
    DataEventPartnerProductPurchase,
    DataEventSeasonPurchase,
    DataEventSubscriptionPurchase,
    DataEventTvShowPurchase,
    DataEventTvShowSetPurchase,
    DataEventVodPurchase,
    DataEventVodSetPurchase,
  ]),
})

//
// Any events
//

/**
 * SSE general event (system or data)
 */
export type AnyEvent = v.InferOutput<typeof AnyEvent> // prettier-ignore
export const AnyEvent = v.variant('event', [SystemEvent, DataEvent])
